<template>
  <div>
    <form v-if="!loadingResources" @submit.prevent="next">
      <h2 class="text-primary text-center m-3">{{ $t("global.select-plan") }}</h2>
      <div class="d-flex align-items-center flex-column">
        <!-- select app from dropdown -->
        <FormAppSelect
          class="my-3"
          name="app"
          v-model="packageFields.appId"
          :apps="apps"
          @input="onAppChange"
        />

        <!--
          select app from list of popular apps
          should just be wordpress, woocommerce, and magento for now
        -->
        <FormPopularAppSelect
          v-if="!packageFields.appId"
          v-model="packageFields.appId"
          class="my-3"
          name="popular-app"
          :apps="apps"
          :popularApps="popularApps"
        />

        <!-- show packages that are compatible with the selected app -->
        <template v-if="packageFields.appId">
          <!--
            for selecting the term (monthly/annual subscription) that the app offers
            should only be visible if the user selected a wordpress or woocommerce app
          -->
          <FormTermFeeToggle
            v-if="showTermToggle && !isBigCommerce"
            :value="packageFields.term"
            @input="onTermChange"
            class="my-3"
          />
          <!--
            does the user have a big commerce account?
            if yes, will ask for creds later during checkout
            should only be visible if the user selected bigcommerce from the apps list
          -->
          <FormBigCommerceAccountToggle
            v-if="isBigCommerce"
            v-model="packageFields.hasBigCommerce"
            class="my-3"
          />

          <!-- list of all packages that are compatible with the selected app -->
          <FormPackageCheckoutSelect
            class="my-3"
            v-model="packageFields.packageId"
            :appType="selectedApp.type"
            :appName="selectedApp.name"
            :appId="packageFields.appId"
            :packages="compatiblePackages"
            :term="packageFields.term"
          />
        </template>

        <template v-if="showErrors">
          <div
            class="text-danger"
            :key="i"
            v-for="(error, i) in validation.fields.packageId.errors"
          >
            {{ error }}
          </div>
        </template>

        <!-- show list of generic packages (no managed app) -->
        <div class="text-primary my-3">
          <span>{{ $t("global.not-finding-plan") }}</span>
          <a class="ml-2" href="#" @click="tryFlexible">{{ $t("global.try-flexible") }}</a>
        </div>
      </div>

      <!-- clicking next will take you to the next step of checkout  -->
      <div class="checkout-action">
        <Btn class="mr-3 mt-4 text-left" type="submit" :loading="loading" variant="primary">
          {{ $t("global.next") }}
        </Btn>
      </div>
    </form>
    <div v-if="loadingResources" class="loader center-middle">
      <LoadingState :size="50" />
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormAppSelect from "@/components/display/form/FormAppSelect";
import FormBigCommerceAccountToggle from "@/components/display/form/FormBigCommerceAccountToggle";
import FormPackageCheckoutSelect from "@/components/display/form/FormPackageCheckoutSelect";
import FormPopularAppSelect from "@/components/display/form/FormPopularAppSelect";
import FormTermFeeToggle from "@/components/display/form/FormTermFeeToggle";
import LoadingState from "@/components/display/LoadingState";
import { checkoutPackageSelection as packageSelectionRules } from "leatherman-js/utils/validations/forms";
import store from "@/store";
import useAnalytics from "@/utils/useAnalytics";
import useValidator from "@/utils/useValidator";

export default {
  name: "CheckoutPackagePage",
  components: {
    Btn,
    FormPackageCheckoutSelect,
    FormBigCommerceAccountToggle,
    FormAppSelect,
    FormTermFeeToggle,
    FormPopularAppSelect,
    LoadingState,
  },
  props: {
    appId: Number,
    packageId: Number,
  },
  setup(props, context) {
    const packageFields = reactive({
      appId: store.state.checkout.appId,
      packageId: store.state.checkout.packageId,
      term: store.state.checkout.term,
      hasBigCommerce: false,
    });

    const { validator } = useValidator();
    const validation = validator(
      packageFields,
      packageSelectionRules,
      {},
      {
        packageId: {
          required: context.root.$t("validation.package-required"),
        },
      }
    );

    // list of all apps available to the customer
    const apps = computed(() => store.getters["apps/getList"]);
    const selectedApp = computed(() => store.getters["apps/getAppById"](packageFields.appId));
    const showTermToggle = computed(() =>
      ["wordpress", "woocommerce"].includes(selectedApp.value?.type)
    );

    // is the selected app bigcommerce?
    const isBigCommerce = computed(() => {
      return selectedApp.value?.type === "bigcommerce";
    });

    // popular apps to be shown in the popular apps menu
    const popularApps = ["wordpress", "woocommerce", "magento"];

    // what appPackages are compatible with the selected app
    const compatiblePackages = computed(() =>
      store.getters["appPackages/getPackagesByApp"](selectedApp.value)
    );

    // shows the user all packages that are compatible with the flexible (generic) app
    const tryFlexible = () => {
      const flexibleApp = apps.value.find((app) => app.type === "generic");
      packageFields.appId = flexibleApp.id;
    };

    const { cartData } = useAnalytics();
    const showErrors = computed(
      () => validation.fields.packageId.dirty && validation.fields.packageId.errors
    );

    const onTermChange = (term) => {
      packageFields.term = term;
      store.dispatch("checkout/setCouponCode", null);
      store.dispatch("checkout/setDiscount", 0);
    };

    const onAppChange = () => {
      packageFields.packageId = null;
      store.dispatch("checkout/setPackage", packageFields.packageId);
      store.dispatch("cartstack/setPackage", packageFields.packageId);

      setTimeout(() => validation.reset(), 10);
    };

    const loading = ref(false);
    // go to next step of checkout
    const next = () => {
      loading.value = true;
      validation.reset();
      validation
        .validate()
        .then(() => {
          // set all the things on the store
          store.dispatch("checkout/setApp", packageFields.appId);
          store.dispatch("checkout/setPackage", packageFields.packageId);
          store.dispatch("checkout/setHasBigCommerce", packageFields.hasBigCommerce);
          store.dispatch("checkout/setTerm", packageFields.term);
          store.dispatch("analytics/send", { name: "addToCart", data: cartData.value });
          store.dispatch("analytics/send", { name: "checkoutStarted", data: cartData.value });

          // continue to the create profile step
          context.root.$router.push({
            name: "checkout-profile",
            query: store.getters["checkout/getQueryParams"],
          });
        })
        .catch(() => {})
        .finally(() => (loading.value = false));
    };

    const loadingResources = ref(true);

    onMounted(() => {
      store
        .dispatch("apps/getList") // get all available apps
        .then(() => store.dispatch("appPackages/getList")) // get all available appPackages
        .then(() => {
          // if the user is not logged in, don't try to fetch their addresses
          if (!store.getters["auth/isLoggedIn"]) {
            return Promise.resolve();
          }

          // get all the user's addresses (including their billing address) to check for sales tax
          return store.dispatch("addresses/getList");
        })
        .finally(() => {
          loadingResources.value = false;

          // if an app and a package has already been selected (perhaps via query string parameters)
          // redirect to the checkout profile step
          if (
            store.state.checkout.appId &&
            store.state.checkout.packageId &&
            store.state.checkout.autoRedirect
          ) {
            // continue to the profile step
            context.root.$router.push({
              name: "checkout-profile",
              query: store.getters["checkout/getQueryParams"],
            });
          }
        });
    });

    return {
      next,
      apps,
      selectedApp,
      packageFields,
      compatiblePackages,
      popularApps,
      showTermToggle,
      isBigCommerce,
      tryFlexible,
      onTermChange,
      onAppChange,
      validation,
      showErrors,
      loadingResources,
      loading,
    };
  },
};
</script>
<style lang="scss">
@import "~@/styles/variables";

.checkout-action {
  display: flex;
  justify-content: flex-end;
}
</style>
